import { RuntimeWidget } from 'client/types';
import { Div } from 'client/widget-components/basicComponents';
import ColorSwatchGroup from 'client/widget-components/components/ColorSwatchGroup';
import Dropdown from 'client/widget-components/components/Dropdown';
import RadioButtonsGroup from 'client/widget-components/components/RadioButtonsGroup';
import { normalizeStyles } from 'client/widgets/common/styles/migration/stylesMigration';
import React from 'react';
import { RuntimeMediaQuery } from '@duda-co/responsive-styles/RuntimeMediaQuery';
import { context } from '@shared-services/context-service';
import { ecomObserver } from '@shared-services/ecom-service';
import withErrorBoundary from '../../widget-components/components/ErrorBoundary/withErrorBoundary';
import { WidgetPlaceholder } from '../../widget-components/components/WidgetPlaceholder';
import { ResponsiveStylesProp } from '../../widget-components/responsive-styles/responsiveStylesService';
import {
    OV_WidgetStyles,
    OptionsRenderType,
    RuntimeOptionsVariationsProps,
} from './types';

const RuntimeOptionsVariations: RuntimeWidget<
    RuntimeOptionsVariationsProps
> = ({
    productState,
    editorEmptyMessage,
    dropdownPlaceholder,
    renderType,
    _styles,
}) => {
        const styles = normalizeStyles({
            stylesInput: _styles,
            monitorKey: 'options.variations',
        }) as OV_WidgetStyles;

        const ovEntries = Object.entries(productState?.dropdownOptions || {});
        if (ovEntries.length === 0) {
            if (context.isEditor && editorEmptyMessage) {
                return (
                    <WidgetPlaceholder data-auto='options-variations-placeholder'>
                        {editorEmptyMessage}
                    </WidgetPlaceholder>
                );
            }
            return <div />;
        }

        return (
            <Div
                styles={[wrapperDefaultStyles, styles?.wrapper]}
                data-auto='options-variations'
                data-grab='options-variations'
                className='options-variations'
            >
                {ovEntries.map(([option, values]) => {
                    const isColorOption = values.every((opt) => opt.color !== undefined);
                    const InnerComponent = getOptionsRenderer(isColorOption ? OptionsRenderType.COLOR : renderType);
                    const selectedValue = productState.getSelectedField(option);
                    const title = isColorOption ? `${option}: ${selectedValue?.value}` : option;
                    return (
                        <InnerComponent
                            key={`d_option_${option}`}
                            selectedValue={selectedValue}
                            options={values}
                            title={title}
                            onChange={(selectedVal) => {
                                productState.updateVariation(
                                    option,
                                    selectedVal.value.toString()
                                );
                            }}
                            direction={styles?.wrapper?.common?.direction}
                            dropdownPlaceholder={dropdownPlaceholder}
                            styles={styles}
                        />
                    );
                })}
            </Div>
        );
    };

const wrapperDefaultStyles: ResponsiveStylesProp = {
    [RuntimeMediaQuery.COMMON]: {
        display: 'flex',
        flexDirection: 'column',
        height: 'fit-content',
        width: '100%',
        padding: '4px',
        gap: '24px',
        border: '0px solid',
    },
};

const getOptionsRenderer = (
    renderType?: OptionsRenderType
) => {
    switch (renderType) {
        case OptionsRenderType.COLOR:
            return ColorSwatchGroup;
        case OptionsRenderType.RADIOBUTTON:
            return RadioButtonsGroup;
        case OptionsRenderType.DROPDOWN:
        default:
            return Dropdown;
    }
};


export default withErrorBoundary({
    Comp: ecomObserver(RuntimeOptionsVariations),
    componentName: 'RuntimeOptionsVariations',
    additionalInfoToLog: {
        tag: 'native-ecom',
    },
});
